import React, { useState, useEffect } from 'react';

const AddressModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialData = null,
  title = "ADD SHIPPING ADDRESS"
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '+234 000 000 0000',
    state: 'FCT',
    city: 'Municipal Area Council',
    postalCode: '900001',
    addressLine1: '',
    addressLine2: '',
    isDefault: false,
    country: 'Nigeria'
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Format phone number (remove non-digits, then format)
    const digitsOnly = value.replace(/\D/g, '');
    let formatted = '+234 ';
    
    if (digitsOnly.length > 0) {
      formatted += digitsOnly.substring(0, 3);
      if (digitsOnly.length > 3) {
        formatted += ' ' + digitsOnly.substring(3, 6);
        if (digitsOnly.length > 6) {
          formatted += ' ' + digitsOnly.substring(6, 10);
        }
      }
    }
    
    setFormData(prev => ({
      ...prev,
      phone: formatted
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Validate required fields
      if (!formData.fullName.trim() || !formData.addressLine1.trim() || 
          !formData.city.trim() || !formData.state.trim() || !formData.postalCode.trim()) {
        throw new Error('Please fill in all required fields');
      }
      
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
      // Error is handled by parent component
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal - Slide from right */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-6 px-6 border-b border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">{title}</h2>
              <p className="text-[#6B635D] text-sm mt-1">Shipping Address</p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded"
              disabled={submitting}
            >
              <svg className="w-6 h-6 text-[#1C2A46]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {/* Country - Read Only */}
              <div>
                <label className="block text-sm font-normal text-secondary mb-2">
                  Country<span className="text-[#D4A574]">*</span>
                </label>
                <div className="p-3 bg-[#F5F5F5] border border-[#E4E4E4] text-gray-700">
                  Nigeria
                </div>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-normal text-secondary mb-2">
                  Full name<span className="text-[#D4A574]">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="enter your full name"
                  className="w-full p-3 bg-[#F5F5F5] border border-[#E4E4E4] placeholder:text-sm placeholder:font-light rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                  required
                  disabled={submitting}
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-normal text-secondary mb-2">
                  Phone number<span className="text-[#D4A574]">*</span>
                </label>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="p-3 bg-[#F5F5F5] border border-[#E4E4E4] border-r-0 flex items-center rounded-l">
                      <span className="text-gray-700">+234</span>
                    </div>
                  </div>
                  <input
                    type="tel"
                    value={formData.phone.replace('+234 ', '')}
                    onChange={handlePhoneChange}
                    className="flex-grow p-3 bg-[#F5F5F5] border border-[#E4E4E4] focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent rounded-r"
                    placeholder="000 000 0000"
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* State/Province */}
              <div>
                <label className="block text-sm font-normal text-secondary mb-2">
                  State/Province<span className="text-[#D4A574]">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full p-3 bg-[#F5F5F5] border border-[#E4E4E4] focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                  required
                  disabled={submitting}
                >
                  <option value="FCT">FCT</option>
                  <option value="Lagos">Lagos</option>
                  <option value="Abia">Abia</option>
                  <option value="Oyo">Oyo</option>
                  <option value="Rivers">Rivers</option>
                  <option value="Kano">Kano</option>
                </select>
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-normal text-secondary mb-2">
                  City<span className="text-[#D4A574]">*</span>
                </label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full p-3 bg-[#F5F5F5] border border-[#E4E4E4] focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                  required
                  disabled={submitting}
                >
                  <option value="Municipal Area Council">Municipal Area Council</option>
                  <option value="Abuja">Abuja</option>
                  <option value="Lagos City">Lagos City</option>
                  <option value="Ibadan">Ibadan</option>
                  <option value="Port Harcourt">Port Harcourt</option>
                  <option value="Kano">Kano</option>
                </select>
              </div>

              {/* Zipcode */}
              <div>
                <label className="block text-sm font-normal text-secondary mb-2">
                  Zipcode<span className="text-[#D4A574]">*</span>
                </label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  placeholder="900001"
                  className="w-full p-3 bg-[#F5F5F5] border border-[#E4E4E4] placeholder:text-sm placeholder:font-light rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                  required
                  disabled={submitting}
                />
              </div>

              {/* Address line */}
              <div>
                <label className="block text-sm font-normal text-secondary mb-2">
                  Address line
                </label>
                <input
                  type="text"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  placeholder="Apartments, suite, building, floor e.t.c"
                  className="w-full p-3 bg-[#F5F5F5] border border-[#E4E4E4] placeholder:text-sm placeholder:font-light rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                  disabled={submitting}
                />
              </div>

              {/* Address line 2 */}
              <div>
                <input
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  placeholder="Street, road, estate e.t.c"
                  className="w-full p-3 bg-[#F5F5F5] border border-[#E4E4E4] placeholder:text-sm placeholder:font-light rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                  disabled={submitting}
                />
              </div>

              {/* Make Default Checkbox */}
              <div className="flex items-center pt-4">
                <input
                  type="checkbox"
                  id="isDefault"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#D4A574] focus:ring-[#D2A146] rounded"
                  disabled={submitting}
                />
                <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                  Make Default
                </label>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddressModal;